/**
 * Admin → backup restore button + dialog.
 *
 * Two-step flow guarded by an explicit confirmation:
 *   1. User picks a backup JSON file. We immediately POST it to
 *      /api/admin/backup/import (mode=dry_run) — the server parses and
 *      counts what would change per collection without touching the DB.
 *   2. The dialog displays the diff. The "Restaurer maintenant" button
 *      is only enabled once a dry-run has succeeded and the user has
 *      typed the literal word "RESTAURER" — a deliberate friction
 *      that makes accidentally clobbering production almost impossible.
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Upload, AlertTriangle, CheckCircle2, RefreshCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminBackupRestore({ token }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [strategy, setStrategy] = useState('upsert');
  const [dryRun, setDryRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [applying, setApplying] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setDryRun(null);
    setConfirmText('');
    setApplying(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setDryRun(null);
    setConfirmText('');
    // Auto-run dry-run as soon as a file is selected.
    await runDryRun(f, strategy);
  };

  const runDryRun = async (f, strat) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await axios.post(
        `${API_URL}/api/admin/backup/import?mode=dry_run&strategy=${strat}`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDryRun(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
      setDryRun(null);
    } finally {
      setLoading(false);
    }
  };

  const onStrategyChange = (s) => {
    setStrategy(s);
    setConfirmText('');
    if (file) runDryRun(file, s);
  };

  const onApply = async () => {
    if (!file) return;
    setApplying(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(
        `${API_URL}/api/admin/backup/import?mode=apply&confirm=true&strategy=${strategy}`,
        fd,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 },
      );
      const t = data.total || {};
      toast.success(
        `Restauration appliquée — ${t.would_insert ?? 0} insertions · ${t.would_update ?? 0} modifications${t.would_delete ? ' · ' + t.would_delete + ' suppressions' : ''}`,
      );
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setApplying(false);
    }
  };

  const canApply = !!dryRun && confirmText === 'RESTAURER' && !applying;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-rose-300 text-rose-700 hover:bg-rose-50"
        data-testid="admin-open-restore-btn"
      >
        <Upload className="w-4 h-4 mr-1.5" />
        Restaurer
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCcw className="w-5 h-5 text-rose-600" />
              Restaurer la base depuis un backup
            </DialogTitle>
            <DialogDescription>
              Étape 1 — sélectionnez un fichier JSON. Le serveur calcule l&apos;impact en lecture seule.
              Étape 2 — relisez le diff, choisissez la stratégie, puis confirmez.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fichier backup JSON</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                onChange={onPickFile}
                className="block w-full text-sm border border-slate-300 rounded-md px-3 py-2 file:mr-3 file:px-3 file:py-1 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                data-testid="admin-restore-file"
              />
              {file && (
                <div className="text-xs text-slate-500 mt-1 font-mono">
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>

            {/* Strategy */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Stratégie</label>
              <div className="flex gap-2">
                <StrategyOpt
                  value="upsert"
                  active={strategy === 'upsert'}
                  onClick={() => onStrategyChange('upsert')}
                  title="Upsert (sûr)"
                  desc="Met à jour les documents du backup. Garde les nouveaux docs en base non présents dans le backup."
                />
                <StrategyOpt
                  value="replace"
                  active={strategy === 'replace'}
                  onClick={() => onStrategyChange('replace')}
                  title="Replace (destructif)"
                  desc="Vide chaque collection avant import. Restaure exactement l'état du backup — perte des modifs récentes."
                />
              </div>
            </div>

            {/* Dry-run result */}
            {loading && (
              <div className="text-sm text-slate-500 py-4 text-center" data-testid="restore-loading">
                Analyse du fichier en cours...
              </div>
            )}
            {dryRun && (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="restore-dry-run">
                <div className="bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center justify-between border-b border-slate-200">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    Dry-run validé — aucun écrit sur la base
                  </span>
                  <span className="font-mono">
                    Backup du {dryRun.backup_metadata?.exported_at?.slice(0, 10) || '—'}
                  </span>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-50/50 text-slate-500">
                    <tr className="text-left">
                      <th className="px-3 py-1.5 font-medium">Collection</th>
                      <th className="px-3 py-1.5 font-medium text-right">DB</th>
                      <th className="px-3 py-1.5 font-medium text-right">Backup</th>
                      <th className="px-3 py-1.5 font-medium text-right text-emerald-700">+ Insert</th>
                      <th className="px-3 py-1.5 font-medium text-right text-amber-700">~ Update</th>
                      <th className="px-3 py-1.5 font-medium text-right text-rose-700">− Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dryRun.summary.map((s) => (
                      <tr key={s.collection} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 font-mono">{s.collection}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{s.current_in_db}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{s.in_backup}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{s.would_insert || ''}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-amber-700">{s.would_update || ''}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-rose-700">{s.would_delete || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Confirmation */}
            {dryRun && (
              <div className="border-2 border-rose-200 bg-rose-50/50 rounded-lg p-3 space-y-2">
                <div className="flex items-start gap-2 text-sm text-rose-800">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    Tapez <code className="px-1.5 py-0.5 bg-white border border-rose-300 rounded font-mono text-xs">RESTAURER</code> dans le champ ci-dessous pour activer le bouton.
                    {strategy === 'replace' && (
                      <strong className="block mt-1">⚠️ Mode &laquo;&nbsp;replace&nbsp;&raquo; — toutes les modifications survenues après le backup seront perdues.</strong>
                    )}
                  </div>
                </div>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  placeholder="Tapez RESTAURER"
                  className="w-full border border-rose-300 rounded-md px-3 py-2 text-sm font-mono uppercase bg-white"
                  data-testid="restore-confirm-input"
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 mt-2">
            <Button type="button" variant="outline" onClick={() => { setOpen(false); reset(); }}>
              Annuler
            </Button>
            <Button
              type="button"
              onClick={onApply}
              disabled={!canApply}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50"
              data-testid="restore-apply-btn"
            >
              {applying ? 'Restauration...' : 'Restaurer maintenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const StrategyOpt = ({ active, onClick, title, desc }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 text-left border rounded-lg p-3 transition ${
      active ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-100' : 'border-slate-200 hover:border-slate-300 bg-white'
    }`}
  >
    <div className={`text-sm font-medium ${active ? 'text-sky-800' : 'text-slate-800'}`}>{title}</div>
    <div className="text-xs text-slate-500 mt-0.5 leading-snug">{desc}</div>
  </button>
);
